import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingEnquiryService from "../services/tradingEnquiryService";

const styles = {
  page: {
    padding: 24,
    minHeight: "100%",
    background: "#f5f6fa",
  },
  panel: {
    maxWidth: 1400,
    margin: "0 auto",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
  },
  header: {
    padding: "20px 24px",
    background: "linear-gradient(90deg, #5f0cff, #7b2cff)",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  body: { padding: 24 },
  input: {
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 7,
    fontSize: 13,
    minWidth: 180,
    boxSizing: "border-box",
    background: "#fff",
    color: "#111827",
  },
  button: {
    padding: "10px 16px",
    borderRadius: 7,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
  },
  primary: {
    padding: "10px 16px",
    borderRadius: 7,
    border: "none",
    background: "#fff",
    color: "#5f0cff",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
  },
  cell: {
    padding: "12px 14px",
    borderBottom: "1px solid #eee",
    textAlign: "left",
    whiteSpace: "nowrap",
    color: "#172033",
  },
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "17px 18px",
    minWidth: 0,
    flex: "1 1 190px",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
  },
  kpiLabel: {
    fontSize: 12,
    color: "#6b7280",
    fontWeight: 600,
    marginBottom: 10,
  },
  kpiValue: {
    fontSize: 27,
    lineHeight: 1.2,
    fontWeight: 750,
    color: "#111827",
  },
};


const formatDate = (value) => {
  if (!value) return "—";

  const raw = String(value).trim();

  // Handle YYYY-MM-DD and ISO date strings.
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;

    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    );

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  // Handle API dates such as:
  // Sun, 25 Oct 2026 00:00:00 GMT
  const date = new Date(raw);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const formatStatus = (value) =>
  String(value || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getStatus = (enquiry) =>
  String(enquiry.status_code || "").toUpperCase();

const statusStyle = (status) => {
  const normalized = String(status || "").toUpperCase();

  const colors = {
    NEW: {
      background: "#dbeafe",
      color: "#1d4ed8",
    },
    IN_PROGRESS: {
      background: "#fef3c7",
      color: "#92400e",
    },
    COSTING: {
      background: "#ede9fe",
      color: "#6d28d9",
    },
    QUOTED: {
      background: "#dcfce7",
      color: "#166534",
    },
    CLOSED: {
      background: "#e5e7eb",
      color: "#374151",
    },
    CANCELLED: {
      background: "#fee2e2",
      color: "#b91c1c",
    },
  };

  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 30,
    fontSize: 11,
    fontWeight: 750,
    whiteSpace: "nowrap",
    ...(colors[normalized] || {
      background: "#f3f4f6",
      color: "#4b5563",
    }),
  };
};

function KpiCard({ label, value, description, accent }) {
  return (
    <div className="trading-enquiry-kpi-card" style={styles.kpiCard}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: 4,
            height: 30,
            borderRadius: 5,
            background: accent,
            flexShrink: 0,
          }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={styles.kpiLabel}>{label}</div>
          <div style={styles.kpiValue}>{value}</div>
        </div>
      </div>
      <div style={{ marginTop: 9, color: "#9ca3af", fontSize: 11 }}>
        {description}
      </div>
    </div>
  );
}

export default function TradingEnquiryListPage() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {};

      if (appliedSearch.trim()) {
        params.search = appliedSearch.trim();
      }

      if (status) {
        params.status = status;
      }

      const response =
        await tradingEnquiryService.getEnquiries(params);

      const data = response.data?.data || response.data;
      const items = Array.isArray(data?.items) ? data.items : [];

      setEnquiries(items);
      setTotal(
        Number.isFinite(Number(data?.total))
          ? Number(data.total)
          : items.length
      );
    } catch (err) {
      setEnquiries([]);
      setTotal(0);
      setError(
        err.response?.data?.message ||
          "Unable to load Trading enquiries."
      );
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, status]);

  useEffect(() => {
    loadEnquiries();
  }, [loadEnquiries]);

  useEffect(() => {
    setPage(1);
  }, [appliedSearch, status, pageSize]);

  const kpis = useMemo(() => {
    const counts = {
      total: enquiries.length,
      new: 0,
      inProgress: 0,
      quoted: 0,
    };

    enquiries.forEach((enquiry) => {
      const currentStatus = getStatus(enquiry);

      if (currentStatus === "NEW") counts.new += 1;
      if (currentStatus === "IN_PROGRESS") counts.inProgress += 1;
      if (currentStatus === "QUOTED") counts.quoted += 1;
    });

    return counts;
  }, [enquiries]);

  const filteredEnquiries = useMemo(() => {
    const term = search.trim().toLowerCase();

    return enquiries.filter((enquiry) => {
      const matchesSearch =
        !term ||
        String(enquiry.enquiry_no || "").toLowerCase().includes(term) ||
        String(enquiry.customer_name || "").toLowerCase().includes(term) ||
        String(enquiry.remarks || "").toLowerCase().includes(term);

      const matchesStatus =
        !status || getStatus(enquiry) === status.toUpperCase();

      return matchesSearch && matchesStatus;
    });
  }, [enquiries, search, status]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEnquiries.length / pageSize)
  );

  const safePage = Math.min(page, totalPages);

  const paginatedEnquiries = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredEnquiries.slice(start, start + pageSize);
  }, [filteredEnquiries, safePage, pageSize]);

  const firstRecord =
    filteredEnquiries.length === 0 ? 0 : (safePage - 1) * pageSize + 1;

  const lastRecord = Math.min(
    safePage * pageSize,
    filteredEnquiries.length
  );

  const applySearch = () => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const openEnquiry = (enquiry) => {
    navigate(`/trading/enquiries/${enquiry.id}`);
  };

  return (
    <div className="trading-enquiry-page" style={styles.page}>
      <style>{`
        .trading-enquiry-page {
          color: #172033;
          background: #f7f9fc !important;
          padding: 24px !important;
          box-sizing: border-box;
        }

        .trading-enquiry-panel {
          max-width: 1400px;
          margin: 0 auto;
          background: transparent !important;
          border: 0 !important;
          border-radius: 0 !important;
          overflow: visible !important;
        }

        .trading-enquiry-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 22px;
          padding: 0 !important;
          background: transparent !important;
          color: #172033 !important;
        }

        .trading-enquiry-breadcrumb {
          color: #778196;
          font-size: 12px;
          margin-bottom: 8px;
        }

        .trading-enquiry-title-row {
          display: flex;
          align-items: center;
          gap: 13px;
        }

        .trading-enquiry-title-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          flex: 0 0 48px;
          border-radius: 14px;
          background: linear-gradient(135deg, #e0edff, #c7dcff);
          color: #075bd8;
          font-size: 25px;
        }

        .trading-enquiry-title {
          margin: 0;
          color: #172033;
          font-size: 27px;
          font-weight: 750;
          letter-spacing: -.5px;
        }

        .trading-enquiry-subtitle {
          color: #778196;
          font-size: 13px;
          margin-top: 5px;
        }

        .trading-enquiry-create-btn {
          border: 0 !important;
          border-radius: 10px !important;
          background: linear-gradient(135deg, #1769e8, #0647bd) !important;
          color: #fff !important;
          padding: 12px 19px !important;
          font-weight: 650 !important;
          box-shadow: 0 5px 14px rgba(23,105,232,.22);
        }

        .trading-enquiry-create-btn:hover {
          filter: brightness(1.06);
        }

        .trading-enquiry-body {
          padding: 0 !important;
        }

        .trading-enquiry-kpi-grid {
          display: grid !important;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px !important;
          margin-bottom: 20px !important;
        }

        .trading-enquiry-kpi-card {
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 116px;
          padding: 20px !important;
          border: 0 !important;
          border-radius: 15px !important;
          color: white !important;
          box-shadow: 0 7px 18px rgba(30,55,100,.12) !important;
        }

        .trading-enquiry-kpi-card::after {
          content: "";
          position: absolute;
          width: 180px;
          height: 115px;
          right: -38px;
          bottom: -72px;
          border-radius: 50%;
          background: rgba(255,255,255,.13);
          transform: rotate(-20deg);
          pointer-events: none;
        }

        .trading-enquiry-kpi-card:nth-child(1) {
          background: linear-gradient(125deg, #20a4f3 0%, #0860e8 72%, #0747c8 100%) !important;
        }

        .trading-enquiry-kpi-card:nth-child(2) {
          background: linear-gradient(125deg, #a78bfa 0%, #8250ed 58%, #6934d8 100%) !important;
        }

        .trading-enquiry-kpi-card:nth-child(3) {
          background: linear-gradient(125deg, #55a7ff 0%, #2878e8 58%, #1556c0 100%) !important;
        }

        .trading-enquiry-kpi-card:nth-child(4) {
          background: linear-gradient(125deg, #42d392 0%, #16a66a 58%, #087c4c 100%) !important;
        }

        .trading-enquiry-kpi-card > div:first-child {
          position: relative;
          z-index: 1;
          gap: 12px !important;
        }

        .trading-enquiry-kpi-card > div:first-child > span {
          width: 44px !important;
          height: 44px !important;
          border-radius: 50% !important;
          background: rgba(255,255,255,.2) !important;
        }

        .trading-enquiry-kpi-card > div:first-child > div > div:first-child {
          color: rgba(255,255,255,.96) !important;
          font-size: 13px !important;
          font-weight: 650 !important;
          margin-bottom: 7px !important;
        }

        .trading-enquiry-kpi-card > div:first-child > div > div:last-child {
          color: #fff !important;
          font-size: 31px !important;
          font-weight: 800 !important;
          line-height: 1 !important;
        }

        .trading-enquiry-kpi-card > div:last-child {
          position: relative;
          z-index: 1;
          color: rgba(255,255,255,.86) !important;
          margin-top: 10px !important;
          font-size: 11px !important;
        }

        .trading-enquiry-filters {
          display: grid !important;
          grid-template-columns: minmax(240px, 1.6fr) minmax(150px, .8fr) auto auto minmax(130px, auto);
          align-items: end !important;
          gap: 13px !important;
          padding: 20px !important;
          margin-bottom: 17px !important;
          border: 1px solid #e6eaf1;
          border-radius: 13px;
          background: #fff;
          box-shadow: 0 3px 12px rgba(21,37,68,.045);
        }

        .trading-enquiry-filters input,
        .trading-enquiry-filters select {
          height: 43px !important;
          border: 1px solid #cfd6e2 !important;
          border-radius: 8px !important;
          padding: 0 12px !important;
          background: #fff !important;
          color: #29354b !important;
          font-size: 13px !important;
          outline: none;
          min-width: 0;
          box-sizing: border-box;
        }

        .trading-enquiry-filters input:focus,
        .trading-enquiry-filters select:focus {
          border-color: #3980f5 !important;
          box-shadow: 0 0 0 3px rgba(57,128,245,.12);
        }

        .trading-enquiry-search-btn,
        .trading-enquiry-refresh-btn {
          height: 43px !important;
          border-radius: 8px !important;
          padding: 0 17px !important;
          font-weight: 650 !important;
          white-space: nowrap;
        }

        .trading-enquiry-search-btn {
          background: linear-gradient(135deg, #1769e8, #0647bd) !important;
          border: 1px solid #0758d5 !important;
          color: #fff !important;
        }

        .trading-enquiry-refresh-btn {
          background: #fff !important;
          border: 1px solid #cbd3df !important;
          color: #263247 !important;
        }

        .trading-enquiry-match-count {
          color: #47536a !important;
          font-size: 12px !important;
        }

        .trading-enquiry-error {
          border-radius: 9px !important;
          padding: 12px 15px !important;
        }

        .trading-enquiry-table-panel {
          width: 100%;
          overflow-x: auto !important;
          border: 1px solid #e6eaf1 !important;
          border-radius: 13px !important;
          background: #fff;
          box-shadow: 0 3px 12px rgba(21,37,68,.045);
        }

        .trading-enquiry-table {
          width: 100% !important;
          min-width: 970px;
          border-collapse: separate !important;
          border-spacing: 0 !important;
          font-size: 13px !important;
        }

        .trading-enquiry-table thead tr {
          background: #f1f4fa !important;
        }

        .trading-enquiry-table th {
          padding: 13px 12px !important;
          text-align: left !important;
          background: #f1f4fa !important;
          color: #29354a !important;
          font-size: 12px !important;
          font-weight: 750 !important;
          border-top: 1px solid #e2e7f0 !important;
          border-bottom: 1px solid #e2e7f0 !important;
          white-space: nowrap;
        }

        .trading-enquiry-table td {
          padding: 13px 12px !important;
          border-bottom: 1px solid #edf0f5 !important;
          font-size: 13px !important;
          color: #29354a !important;
          white-space: nowrap;
        }

        .trading-enquiry-table tbody tr:hover td {
          background: #f8faff !important;
        }

        .trading-enquiry-table .trading-enquiry-code {
          color: #145ac7 !important;
          font-weight: 700;
        }

        .trading-enquiry-table .trading-enquiry-status {
          display: inline-flex !important;
          align-items: center;
          border-radius: 30px !important;
          padding: 5px 10px !important;
          font-size: 11px !important;
          font-weight: 750 !important;
        }

        .trading-enquiry-view-btn {
          border: 1px solid #cbd9f4 !important;
          background: #eef4ff !important;
          color: #145ac7 !important;
          border-radius: 7px !important;
          padding: 6px 12px !important;
          cursor: pointer;
          font-size: 11px !important;
          font-weight: 700 !important;
        }

        .trading-enquiry-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 18px;
          padding-top: 0 !important;
        }

        .trading-enquiry-pagination select {
          height: 35px !important;
          border-radius: 7px !important;
        }

        .trading-enquiry-pagination button {
          min-height: 35px;
          border: 1px solid #dce2ec !important;
          border-radius: 7px !important;
          background: #fff !important;
          color: #33415a !important;
          padding: 0 12px !important;
        }

        .trading-enquiry-pagination button:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        @media (max-width: 1100px) {
          .trading-enquiry-kpi-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .trading-enquiry-filters {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .trading-enquiry-match-count {
            margin-left: 0 !important;
          }
        }

        @media (max-width: 576px) {
          .trading-enquiry-page {
            padding: 14px !important;
          }

          .trading-enquiry-kpi-grid,
          .trading-enquiry-filters {
            grid-template-columns: 1fr;
          }

          .trading-enquiry-title {
            font-size: 22px;
          }

          .trading-enquiry-filters {
            padding: 13px !important;
          }
        }
      `}</style>

      <div className="trading-enquiry-panel" style={styles.panel}>
        <div
          className="trading-enquiry-heading"
          style={styles.header}
        >
          <div>
            <div className="trading-enquiry-breadcrumb">
              Trading <span style={{ margin: "0 8px" }}>›</span>
              <strong>Enquiries</strong>
            </div>

            <div className="trading-enquiry-title-row">
              <span className="trading-enquiry-title-icon">▤</span>

              <div>
                <div
                  className="trading-enquiry-title"
                  style={{ fontSize: 21, fontWeight: 700 }}
                >
                  Trading Enquiries
                </div>

                <div
                  className="trading-enquiry-subtitle"
                  style={{ fontSize: 12, marginTop: 5 }}
                >
                  Customer requirements for raw materials
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="trading-enquiry-create-btn"
            style={styles.primary}
            onClick={() => navigate("/trading/enquiries/create")}
          >
            + Create Enquiry
          </button>
        </div>

        <div
          className="trading-enquiry-body"
          style={styles.body}
        >
          {/* KPI CARDS */}
          <div
            className="trading-enquiry-kpi-grid"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 14,
              marginBottom: 24,
            }}
          >
            <KpiCard
              label="Total Enquiries"
              value={loading ? "—" : kpis.total}
              description="Records currently loaded"
              accent="#6d28d9"
            />

            <KpiCard
              label="New"
              value={loading ? "—" : kpis.new}
              description="Awaiting initial action"
              accent="#7c3aed"
            />

            <KpiCard
              label="In Progress"
              value={loading ? "—" : kpis.inProgress}
              description="Being worked on"
              accent="#2563eb"
            />

            <KpiCard
              label="Quoted"
              value={loading ? "—" : kpis.quoted}
              description="Quotation status"
              accent="#16a34a"
            />
          </div>

          {/* SEARCH AND FILTERS */}
          <div
            className="trading-enquiry-filters"
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 16,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              className="trading-enquiry-search-input"
              style={{
                ...styles.input,
                flex: "1 1 240px",
                maxWidth: 340,
              }}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") applySearch();
              }}
              placeholder="Search enquiry no., customer or remarks"
              aria-label="Search Trading enquiries"
            />

            <select
              className="trading-enquiry-status-filter"
              style={styles.input}
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="NEW">New</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COSTING">Costing</option>
              <option value="QUOTED">Quoted</option>
              <option value="CLOSED">Closed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>

            <button
              type="button"
              className="trading-enquiry-search-btn"
              style={styles.button}
              onClick={applySearch}
              disabled={loading}
            >
              Search
            </button>

            <button
              type="button"
              className="trading-enquiry-refresh-btn"
              style={styles.button}
              onClick={loadEnquiries}
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>

            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
                marginLeft: "auto",
              }}
              className="trading-enquiry-match-count"
            >
              {filteredEnquiries.length} matching record
              {filteredEnquiries.length === 1 ? "" : "s"}
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="trading-enquiry-error"
              style={{
                padding: 12,
                marginBottom: 16,
                background: "#fef2f2",
                color: "#b91c1c",
                border: "1px solid #fecaca",
                borderRadius: 7,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {/* ENQUIRY TABLE */}
          <div
            className="trading-enquiry-table-panel"
            style={{
              overflowX: "auto",
              border: "1px solid #edf0f4",
              borderRadius: 8,
            }}
          >
            <table
              className="trading-enquiry-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {[
                    "Enquiry No.",
                    "Customer",
                    "Enquiry Date",
                    "Expected Response",
                    "Priority",
                    "Status",
                    "Remarks",
                    "Action",
                  ].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        ...styles.cell,
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#374151",
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {!loading &&
                  paginatedEnquiries.map((enquiry) => (
                    <tr
                      key={enquiry.id}
                      onClick={() => openEnquiry(enquiry)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") openEnquiry(enquiry);
                      }}
                      tabIndex={0}
                      title="Open enquiry details"
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(event) => {
                        event.currentTarget.style.background = "#faf8ff";
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.background = "#fff";
                      }}
                    >
                      <td style={styles.cell}>
                        <strong
                          className="trading-enquiry-code"
                          style={{ color: "#5f0cff" }}
                        >
                          {enquiry.enquiry_no || "—"}
                        </strong>
                      </td>

                      <td style={styles.cell}>
                        {enquiry.customer_name || "—"}
                      </td>

                      <td style={styles.cell}>
                        {formatDate(enquiry.enquiry_date)}
                      </td>

                      <td style={styles.cell}>
                        {formatDate(enquiry.expected_response_date)}
                      </td>

                      <td style={styles.cell}>
                        {formatStatus(enquiry.priority_code)}
                      </td>

                      <td style={styles.cell}>
                        <span
                          className="trading-enquiry-status"
                          style={statusStyle(enquiry.status_code)}
                        >
                          {formatStatus(enquiry.status_code)}
                        </span>
                      </td>

                      <td
                      style={{
                        ...styles.cell,
                        width: 220,
                        minWidth: 180,
                        maxWidth: 220,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        lineHeight: 1.5,
                      }}
                    >
                      {enquiry.remarks || "—"}
                    </td>

                      <td style={styles.cell}>
                        <button
                          type="button"
                          className="trading-enquiry-view-btn"
                          style={{
                            border: "1px solid #ddd6fe",
                            background: "#f5f3ff",
                            color: "#5b21b6",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            openEnquiry(enquiry);
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}

                {!loading && paginatedEnquiries.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: 36,
                        textAlign: "center",
                        color: "#6b7280",
                      }}
                    >
                      {error
                        ? "Enquiries could not be loaded."
                        : "No Trading enquiries found."}
                    </td>
                  </tr>
                )}

                {loading && (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: 36,
                        textAlign: "center",
                        color: "#6b7280",
                      }}
                    >
                      Loading Trading enquiries...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div
            className="trading-enquiry-pagination"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 14,
              flexWrap: "wrap",
              paddingTop: 18,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                color: "#6b7280",
                fontSize: 12,
              }}
            >
              <span>Rows per page</span>

              <select
                value={pageSize}
                onChange={(event) =>
                  setPageSize(Number(event.target.value))
                }
                style={{
                  ...styles.input,
                  minWidth: 75,
                  padding: "7px 9px",
                }}
                aria-label="Rows per page"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>

              <span>
                Showing {firstRecord}–{lastRecord} of{" "}
                {filteredEnquiries.length}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <button
                type="button"
                style={{
                  ...styles.button,
                  padding: "8px 12px",
                  opacity: safePage <= 1 || loading ? 0.45 : 1,
                  cursor:
                    safePage <= 1 || loading ? "not-allowed" : "pointer",
                }}
                disabled={safePage <= 1 || loading}
                onClick={() => setPage(1)}
              >
                First
              </button>

              <button
                type="button"
                style={{
                  ...styles.button,
                  padding: "8px 12px",
                  opacity: safePage <= 1 || loading ? 0.45 : 1,
                  cursor:
                    safePage <= 1 || loading ? "not-allowed" : "pointer",
                }}
                disabled={safePage <= 1 || loading}
                onClick={() =>
                  setPage((current) => Math.max(1, current - 1))
                }
              >
                Previous
              </button>

              <span
                style={{
                  padding: "0 8px",
                  fontSize: 12,
                  color: "#374151",
                  whiteSpace: "nowrap",
                }}
              >
                Page {safePage} of {totalPages}
              </span>

              <button
                type="button"
                style={{
                  ...styles.button,
                  padding: "8px 12px",
                  opacity:
                    safePage >= totalPages || loading ? 0.45 : 1,
                  cursor:
                    safePage >= totalPages || loading
                      ? "not-allowed"
                      : "pointer",
                }}
                disabled={safePage >= totalPages || loading}
                onClick={() =>
                  setPage((current) =>
                    Math.min(totalPages, current + 1)
                  )
                }
              >
                Next
              </button>

              <button
                type="button"
                style={{
                  ...styles.button,
                  padding: "8px 12px",
                  opacity:
                    safePage >= totalPages || loading ? 0.45 : 1,
                  cursor:
                    safePage >= totalPages || loading
                      ? "not-allowed"
                      : "pointer",
                }}
                disabled={safePage >= totalPages || loading}
                onClick={() => setPage(totalPages)}
              >
                Last
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}